import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import agentService from '../../services/agentService';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [evRes, txRes] = await Promise.all([
          agentService.getAssignedEvents(),
          agentService.getTransactions(),
        ]);
        setEvents(evRes.data?.data || []);
        setTransactions((txRes.data?.data || []).slice(0, 10));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleScan = (eventId) => navigate('/agent/scan', { state: { event_id: eventId } });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const checkinCount = transactions.filter(t => t.transaction_type === 'checkin').length;
  const checkoutCount = transactions.filter(t => t.transaction_type === 'checkout').length;

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {user?.name}</h1>
          <p className="text-gray-400 text-sm mt-0.5">Here's what's happening today.</p>
        </div>
        <img
          src={`${process.env.PUBLIC_URL}/ticketaty-logo.svg`}
          alt="Ticketaty"
          className="h-7 w-auto opacity-30 hidden sm:block"
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{events.length}</p>
          <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wide">Assigned Events</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{checkinCount}</p>
          <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wide">Check-ins</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-orange-500">{checkoutCount}</p>
          <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wide">Check-outs</p>
        </div>
      </div>

      {/* Assigned Events */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3">Assigned Events</h2>
        {events.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center text-gray-400 text-sm">
            No events assigned yet.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col"
              >
                {ev.image_path ? (
                  <img
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/storage/${ev.image_path}`}
                    alt={ev.title}
                    className="w-full h-36 object-cover"
                  />
                ) : (
                  <div className="w-full h-36 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <svg className="w-10 h-10 text-blue-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                    </svg>
                  </div>
                )}
                <div className="p-4 flex flex-col flex-1 gap-2">
                  <h3 className="font-semibold text-gray-800 text-sm leading-snug">{ev.title}</h3>
                  <div className="text-xs text-gray-400 space-y-0.5">
                    <p>{new Date(ev.event_start).toLocaleString()} — {new Date(ev.event_end).toLocaleString()}</p>
                    {ev.location && <p>{ev.location}</p>}
                    {ev.gate_assigned && (
                      <span className="inline-block bg-blue-50 text-blue-600 font-medium px-2 py-0.5 rounded-full text-xs mt-1">
                        Gate: {ev.gate_assigned}
                      </span>
                    )}
                  </div>
                  <div className="mt-auto pt-2">
                    {ev.event_ended ? (
                      <span className="inline-block text-xs font-medium text-red-400 bg-red-50 px-3 py-1.5 rounded-lg w-full text-center">
                        Event ended
                      </span>
                    ) : (
                      <button
                        onClick={() => handleScan(ev.id)}
                        className="w-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        Scan Tickets
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent Activity */}
      {transactions.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-gray-700 mb-3">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-400 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Attendee</th>
                  <th className="px-4 py-3 text-left">Event</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{tx.attendee_name}</p>
                      <p className="text-gray-400 text-xs">{tx.attendee_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs truncate max-w-[140px]">{tx.event_title}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          tx.transaction_type === 'checkin'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {tx.transaction_type === 'checkin' ? 'Check In' : 'Check Out'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                      {new Date(tx.transaction_datetime).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
